import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { createPortal } from 'react-dom';
import { useEffect } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { GripVertical, Loader2 } from 'lucide-react';
import { useConfiguracaoCampos } from '@/features/relatorios/hooks/useConfiguracaoCampos';
import { ColunaLista } from './ColunaLista';
import { PassoIndicador } from './PassoIndicador';

interface ModalConfiguracaoProps {
  aberto: boolean;
  onFechar: () => void;
  relatorioId: string | null;
  relatorioNome?: string;
}

export function ModalConfiguracao({
  aberto,
  onFechar,
  relatorioId,
  relatorioNome = 'Relatório',
}: ModalConfiguracaoProps) {
  const {
    activeCampo,
    activeIsVisivel,
    camposCondicaoDisponiveis,
    camposExcluidos,
    camposOrdenacao,
    camposSeparacao,
    camposVisiveis,
    condicaoTotalizadorCampoId,
    handleAvancar,
    handleDragEnd,
    handleDragOver,
    handleDragStart,
    handleSalvar,
    handleVoltar,
    isLoading,
    isSaving,
    passo,
    selectedItems,
    setCondicaoTotalizador,
    toggleSelection,
    totalizadoresExcluidos,
    totalizadoresIncluidos,
  } = useConfiguracaoCampos({ relatorioId, onSaveSuccess: onFechar });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const SEM_CONDICAO = 'nenhuma';

  useEffect(() => {
    if (!activeCampo) return;

    const previousBodyCursor = document.body.style.cursor;
    const previousRootCursor = document.documentElement.style.cursor;

    document.body.style.cursor = 'grabbing';
    document.documentElement.style.cursor = 'grabbing';

    return () => {
      document.body.style.cursor = previousBodyCursor;
      document.documentElement.style.cursor = previousRootCursor;
    };
  }, [activeCampo]);

  return (
    <Dialog open={aberto} onOpenChange={open => !open && onFechar()}>
      <DialogContent className="flex h-[92vh] w-[96vw]! max-w-[96vw]! sm:w-[96vw]! sm:max-w-[96vw]! flex-col p-0">
        <DialogHeader className="border-b px-6 pb-4 pt-6">
          <DialogTitle className="text-2xl">Configuração de Relatório</DialogTitle>
          <p className="mt-1 text-sm text-muted-foreground">{relatorioNome}</p>
        </DialogHeader>

        <PassoIndicador passoAtual={passo} />

        {isLoading ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Carregando configuração do relatório...</p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            <div className="flex min-h-0 flex-1 flex-col px-6 py-4">
              {passo === 3 && (
                <div className="mb-4 shrink-0 space-y-1.5">
                  <label className="text-sm font-medium">
                    Coluna de condição do totalizador (opcional)
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Quando marcada, cada totalizador ao lado sai quebrado por valor desta coluna
                    (ex.: S / N), mais uma linha de Total Geral — em vez de uma soma só.
                  </p>
                  <Select
                    value={
                      condicaoTotalizadorCampoId ? String(condicaoTotalizadorCampoId) : SEM_CONDICAO
                    }
                    onValueChange={valor =>
                      setCondicaoTotalizador(valor === SEM_CONDICAO ? null : Number(valor))
                    }
                  >
                    <SelectTrigger className="w-full max-w-md">
                      <SelectValue placeholder="Nenhuma" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={SEM_CONDICAO}>Nenhuma</SelectItem>
                      {camposCondicaoDisponiveis.map(campo => (
                        <SelectItem key={campo.Campo_id} value={String(campo.Campo_id)}>
                          {campo.Descricao_Simplificada_Campo || campo.Nome_Campo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="grid h-full min-h-0 grid-cols-2 gap-6">
                {passo === 1 ? (
                  <>
                    <ColunaLista
                      titulo="Colunas visíveis no relatório"
                      campos={camposVisiveis}
                      isVisivel
                      selectedItems={selectedItems}
                      onToggleSelection={toggleSelection}
                      droppableId="visiveis-droppable"
                    />
                    <ColunaLista
                      titulo="Colunas excluídas do relatório"
                      campos={camposExcluidos}
                      droppableId="excluidos-droppable"
                    />
                  </>
                ) : passo === 2 ? (
                  <>
                    <ColunaLista
                      titulo="Ordenar as colunas da separação"
                      campos={camposSeparacao}
                      droppableId="separacao-droppable"
                    />
                    <ColunaLista
                      titulo="Considerar ordem do relatório"
                      campos={camposOrdenacao}
                      droppableId="ordenacao-droppable"
                    />
                  </>
                ) : (
                  <>
                    <ColunaLista
                      titulo="Totalizadores considerados no relatório"
                      campos={totalizadoresIncluidos}
                      droppableId="totalizadores-incluidos-droppable"
                    />
                    <ColunaLista
                      titulo="Totalizadores excluídos do relatório"
                      campos={totalizadoresExcluidos}
                      droppableId="totalizadores-excluidos-droppable"
                    />
                  </>
                )}
              </div>
            </div>

            {createPortal(
              <DragOverlay
                dropAnimation={{
                  duration: 200,
                  easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
                }}
              >
                {activeCampo ? (
                  <div className="flex cursor-grabbing items-center gap-3 rounded-lg border-2 border-primary bg-card p-3 shadow-2xl ring-4 ring-primary/30">
                    <GripVertical className="h-5 w-5 animate-pulse text-primary" />
                    {activeIsVisivel && (
                      <Checkbox
                        checked={selectedItems.has(activeCampo.Campo_id)}
                        className="pointer-events-none shrink-0"
                      />
                    )}
                    <span className="flex-1 truncate text-sm font-medium">
                      {activeCampo.Descricao_Campo}
                    </span>
                  </div>
                ) : null}
              </DragOverlay>,
              document.body
            )}
          </DndContext>
        )}

        <div className="flex shrink-0 items-center justify-between border-t bg-muted/10 px-6 pb-6 pt-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Checkbox className="pointer-events-none" checked />
            <span>
              {passo === 1
                ? 'Campos para separar em grupos'
                : passo === 2
                  ? 'Campos para considerar na ordenação'
                  : 'Arraste os campos para configurar os totalizadores'}
            </span>
          </div>
          <div className="flex gap-3">
            {(passo === 2 || passo === 3) && (
              <Button variant="outline" onClick={handleVoltar} disabled={isSaving}>
                Voltar
              </Button>
            )}
            {passo === 1 && (
              <Button variant="outline" onClick={onFechar}>
                Cancelar
              </Button>
            )}
            <Button onClick={passo === 3 ? handleSalvar : handleAvancar} disabled={isSaving}>
              {passo === 3 ? (isSaving ? 'Salvando...' : 'Salvar') : 'Avançar'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
